import React, { useRef, useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Button, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Header from "../../Includes/Header";
import Footer from "../../Includes/Footer";
import { ToastBox, notifyError } from "MyComponents/Toaster";
import reg_user from "../../../images/reg_user.png";
import reg_entity from "../../../images/reg_entity.png";
import OTPInput from "../../UI/OtpInput";
import ResendCode from "../../UI/ResendCode";
import { resendOtp } from "../../../Utils/otp";
import { linkEntity } from "../../../Utils/entityLink";

interface LinkEntityVerificationProps {
  userId: string;
  email: string;
  entityName: string | null;
  entityId?: string;
  invitationId?: string;
  relationshipNoun: string;
  onBack: () => void;
}

const PURPOSE = "link_entity";

const LinkEntityVerification: React.FC<LinkEntityVerificationProps> = ({
  userId,
  email,
  entityName,
  entityId,
  invitationId,
  relationshipNoun,
  onBack,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const num1 = useRef<HTMLInputElement>(null);
  const num2 = useRef<HTMLInputElement>(null);
  const num3 = useRef<HTMLInputElement>(null);
  const num4 = useRef<HTMLInputElement>(null);
  const num5 = useRef<HTMLInputElement>(null);
  const num6 = useRef<HTMLInputElement>(null);
  const refs = [num1, num2, num3, num4, num5, num6];

  const [timer, setTimer] = useState(30);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [validated, setValidated] = useState(false);
  const [validatedEmpty, setValidatedEmpty] = useState(false);
  const [validatedSuccess, setValidatedSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (resendEnabled) return;
    if (timer <= 0) {
      setResendEnabled(true);
      return;
    }
    const interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    return () => clearInterval(interval);
  }, [timer, resendEnabled]);

  const fieldsEmpty = useCallback(() => {
    refs.forEach((ref) => {
      if (ref.current) ref.current.value = "";
    });
  }, [refs]);

  const clearMessages = () => {
    setValidated(false);
    setValidatedEmpty(false);
    setValidatedSuccess(false);
    setErrorMessage("");
  };

  const handleOtpInput = (e: React.ChangeEvent<HTMLInputElement>, nextInputRef: React.RefObject<HTMLInputElement> | null) => {
    const value = e.target.value;
    clearMessages();
    if (value.length === 1 && /^\d$/.test(value)) {
      if (nextInputRef?.current) nextInputRef.current.focus();
    } else {
      e.target.value = "";
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData("text");
    if (/^\d+$/.test(pastedText) && pastedText.length === 6) {
      const otpArray = pastedText.split("");
      refs.forEach((ref, index) => {
        if (ref.current) ref.current.value = otpArray[index];
      });
      num6.current?.focus();
      e.preventDefault();
    }
  };

  const handleBackspace = (currentRef: React.RefObject<HTMLInputElement>, prevRef: React.RefObject<HTMLInputElement> | null) => {
    if (currentRef.current?.value === "" && prevRef?.current) {
      prevRef.current.focus();
      prevRef.current.value = "";
    } else if (currentRef.current) {
      currentRef.current.value = "";
    }
  };

  const resendCode = async () => {
    clearMessages();
    const data = await resendOtp(email, PURPOSE);
    if (data?.status === 1 || data?.nextResendAt) {
      fieldsEmpty();
      setValidatedSuccess(true);
      const seconds = data.nextResendAt
        ? Math.max(Math.ceil((new Date(data.nextResendAt).getTime() - Date.now()) / 1000), 1)
        : 30;
      setResendEnabled(false);
      setTimer(seconds);
    } else if (data?.retryAfterSeconds != null) {
      setResendEnabled(false);
      setTimer(data.retryAfterSeconds);
      setErrorMessage(t('common.try_again_in', 'Please wait before requesting another code.'));
    } else {
      setErrorMessage('Something went wrong sending the code. Please try again.');
    }
  };

  const submitForm = async () => {
    clearMessages();
    const otp = refs.map((r) => r.current?.value ?? "").join("");

    if (!otp || otp.length < 6) {
      setValidatedEmpty(true);
      return;
    }

    setIsSubmitted(true);
    setLoading(true);
    const { data, error, code } = await linkEntity(userId, email, otp, { entityId, invitationId });
    setLoading(false);
    setIsSubmitted(false);

    if (error) {
      fieldsEmpty();
      if (code === "ALREADY_LINKED") {
        notifyError(error);
        onBack();
      } else {
        setValidated(true);
      }
      return;
    }

    if (data?.profile?.wso2_user_id && data?.profile?.kyc_token) {
      navigate(`/kyc/personal/${encodeURIComponent(data.profile.wso2_user_id)}/${encodeURIComponent(data.profile.kyc_token)}`);
    }
  };

  return (
    <>
      <ToastBox />
      <Header />
      <Container fluid className="min-vh-custom">
        <Row className="mt-5 px-3 px-md-5">
          <Col md={12} className="text-center">
            <h4 className="fw-bold">{t('register.link_entity_title')}</h4>
            <p className="text-secondary mb-4">
              {t('register.link_entity_subtitle', { relationship: relationshipNoun, entityName: entityName || t('register.this_business', 'this business') })}
            </p>

            <Row className="justify-content-center align-items-center g-3 mb-4">
              <Col xs={5} md={4} lg={3}>
                <div className="p-3 rounded-4 border text-center h-100">
                  <img src={reg_user} width={48} alt="Your account" />
                  <p className="fw-bold small mt-2 mb-0">{t('register.you')}</p>
                  <p className="text-secondary small mb-0 text-truncate">{email}</p>
                </div>
              </Col>
              <Col xs="auto">
                <i className="fa-solid fa-link text-primary fs-4"></i>
              </Col>
              <Col xs={5} md={4} lg={3}>
                <div className="p-3 rounded-4 border text-center h-100">
                  <img src={reg_entity} width={48} alt={entityName || "Entity"} />
                  <p className="fw-bold small mt-2 mb-0 text-truncate">{entityName || t('register.this_business', 'This business')}</p>
                  <p className="text-secondary small mb-0 text-capitalize">{relationshipNoun.replace("an ", "").replace("a ", "")}</p>
                </div>
              </Col>
            </Row>

            <p className="fs-6 letter-spacing-fix fw-medium">
              {t('register.enter_link_code', { email })}
            </p>
            <Form noValidate onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
              <div className="input-group d-flex justify-content-center">
                <div className="input-group d-flex justify-content-center">
                  <OTPInput inputRef={num1} nextRef={num2} prevRef={null} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                  <OTPInput inputRef={num2} nextRef={num3} prevRef={num1} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                  <OTPInput inputRef={num3} nextRef={num4} prevRef={num2} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                  <OTPInput inputRef={num4} nextRef={num5} prevRef={num3} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                  <OTPInput inputRef={num5} nextRef={num6} prevRef={num4} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                  <OTPInput inputRef={num6} nextRef={null} prevRef={num5} handleOtpInput={handleOtpInput} handleBackspace={handleBackspace} handlePaste={handlePaste} />
                </div>
                {validated && <p className="text-danger fw-bold small text-start mt-2 mb-0">{t('common.otp_invalid')}</p>}
                {validatedEmpty && <p className="text-danger fw-bold small text-start mt-2 mb-0">{t('common.otp_enter_valid')}</p>}
                {validatedSuccess && <p className="text-success fw-bold small text-start mt-2 mb-0">{t('common.otp_sent')}</p>}
                {errorMessage && <p className="text-danger fw-bold small text-start mt-2 mb-0">{errorMessage}</p>}
              </div>
              <div className="d-grid gap-2 col-md-3 mx-auto mt-3">
                <Button variant="primary" type="submit" className="btn btn-primary rounded-5 mt-3" disabled={loading && isSubmitted}>
                  {loading && isSubmitted && (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="mx-2" />
                  )}
                  <span>{t('register.confirm_link')}</span>
                </Button>
              </div>
            </Form>
            <ResendCode timer={timer} resendEnabled={resendEnabled} resendCode={resendCode} />
            <p className="mt-3">
              <span className="text-primary" style={{ cursor: "pointer" }} onClick={onBack}>
                {t('register.use_different_email')}
              </span>
            </p>
          </Col>
        </Row>
      </Container>
      <Footer full={true} />
    </>
  );
};

export default LinkEntityVerification;
